import React, { useState, useEffect, useRef } from 'react';
import LoadingSpinner from '../components/LoadingSpinner';
import { useNavigate, useParams } from 'react-router-dom';
import { post, get } from 'aws-amplify/api';

function CreateGuestScreen() {
  const { eventId } = useParams();

  const navigate = useNavigate();
  
  const [firstName, setFirstName] = useState('');
  const [shortName, setShortName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [tag, setTag] = useState('');
  const [tags, setTags] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [isLoadingTags, setIsLoadingTags] = useState(false);
  const [showTagSuggestions, setShowTagSuggestions] = useState(false);
  const [rsvpStatus, setRsvpStatus] = useState('Unconfirmed');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const tagInputRef = useRef(null);

  // Function to fetch event tags
  const fetchEventTags = async () => {
    if (isLoadingTags) return;
    
    try {
      setIsLoadingTags(true);
      const restOperation = get({
        apiName: 'events',
        path: `/guests/${eventId}/tags`,
        withCredentials: true
      });
      await restOperation.response;
      const { body } = await restOperation.response;
      const json = await body.json();
      console.log('Got tags response:', json);
      setAvailableTags(Array.isArray(json) ? json : []);
    } catch (error) {
      console.error('Error fetching tags:', error);
    } finally {
      setIsLoadingTags(false);
    }
  };
  
  // Close tag suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (tagInputRef.current && !tagInputRef.current.contains(event.target)) {
        setShowTagSuggestions(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [tagInputRef]);
  
  const handleCreateGuest = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!firstName && !shortName && !lastName) {
      setError('At least one name is required');
      return;
    }
    
    try {
      setIsSubmitting(true);
      
      const newGuest = {
        event_id: eventId,
        firstName: firstName || null,
        shortName: shortName || null,
        lastName: lastName || null,
        email: email || null,
        phone: phone || null,
        rsvpStatus: rsvpStatus,
        tags: tags
      };

      console.log('Sending POST:', JSON.stringify(newGuest));

      const restOperation = post({
        apiName: 'events',
        path: '/guests',
        withCredentials: true,        
        options: {
          body: JSON.stringify(newGuest),
          headers: {
            'Content-Type': 'application/json'
          }
        }
      });
      await restOperation.response;
      const { body } = await restOperation.response;
      const json = await body.json();
      console.log('Guest created:', json);
      
      // Navigate back to event with the new guest
      navigate(`/guests/${eventId}`, { state: { newGuest: json } });
    } catch (error) {
      console.error('Error creating guest:', error);
      setError('Failed to create guest. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="content">
      <h1 className="content__heading">Add New Guest</h1>
      
      {error && (
        <div className="message message--error">
          {error}
        </div>
      )}
      
      <form onSubmit={handleCreateGuest} className="form">
        <div>
          <label htmlFor="firstName" className="form__label">
            First Name (optional, at least one name required)
          </label>
          <input
            type="text"
            id="firstName"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            className="form__input"
          />
        </div>

        <div>
          <label htmlFor="shortName" className="form__label">
            Short Name (optional, at least one name required)
          </label>
          <input
            type="text"
            id="shortName"
            value={shortName}
            onChange={(e) => setShortName(e.target.value)}
            className="form__input"
          />
        </div>

        <div>
          <label htmlFor="lastName" className="form__label">
            Last Name (optional, at least one name required)
          </label>
          <input
            type="text"
            id="lastName"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            className="form__input"
          />
        </div>
        
        <div>
          <label htmlFor="email" className="form__label">
            Email
          </label>
          <input
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="form__input"
          />
        </div>
        
        <div>
          <label htmlFor="phone" className="form__label">
            Phone Number
          </label>
          <input
            type="tel"
            id="phone"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className="form__input"
          />
        </div>

        <div>
          <label htmlFor="tag" className="form__label">
            Tag
          </label>
          <div className="tag__container">
            <input
              type="text"
              id="tag"
              ref={tagInputRef}
              value={tag}
              onChange={(e) => setTag(e.target.value)}
              onFocus={() => {
                setShowTagSuggestions(true);
                // Only fetch tags when the field is clicked
                fetchEventTags();
              }}
              className="form__input"
            />
            {showTagSuggestions && availableTags.length > 0 && (
              <div className="tag__suggestions">
                {availableTags
                  .filter(t => t.toLowerCase().includes(tag.toLowerCase()))
                  .map((suggestedTag, index) => (
                    <div
                      key={index}
                      className="tag__suggestion"
                      onClick={() => {
                        setTag("");
                        if (!tags.includes(suggestedTag)) {
                          setTags([...tags, suggestedTag]);
                        }
                        setShowTagSuggestions(false);
                      }}
                    >
                      {suggestedTag}
                    </div>
                  ))}
              </div>
            )}
          </div>
          <div className="tag__list">
            {tags.map((t, index) => (
              <div 
                key={index} 
                className="tag"
              >
                {t}
                <button
                  type="button"
                  onClick={() => {
                    setTags(tags.filter((_, i) => i !== index));
                  }}
                  className="tag__remove"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
          <div className="form__group">
            <button
              type="button"
              className="button button--text"
              onClick={() => {
                if (tag.trim() && !tags.includes(tag.trim())) {
                  setTags([...tags, tag.trim()]);
                  setTag("");
                  tagInputRef.current.focus();
                }
              }}
            >
              Add Tag
            </button>
          </div>
        </div>
        
        <div>
          <label htmlFor="rsvpStatus" className="form__label">
            RSVP Status
          </label>
          <select
            id="rsvpStatus"
            value={rsvpStatus}
            onChange={(e) => setRsvpStatus(e.target.value)}
            className="form__input"
          >
            <option value="Confirmed">Confirmed</option>
            <option value="Declined">Declined</option>
            <option value="Viewed">Viewed</option>
            <option value="Unconfirmed">Unconfirmed</option>
            <option value="Cancelled">Cancelled</option>
          </select>
        </div>
        
        <div className="actions">
          <button
            type="button"
            onClick={() => navigate(`/event/${eventId}`)}
            className="button button--neutral"
          >
            Cancel
          </button>
          
          <button
            type="submit"
            disabled={isSubmitting}
            className="button button--primary"
          >
            {isSubmitting ? 'Creating...' : 'Create Guest'}
          </button>
        </div>
      </form>
      
      {isSubmitting && <LoadingSpinner message="Creating guest..." />}
    </div>
  );
}

export default CreateGuestScreen;